import React, { useState, useEffect } from 'react';
import { useAssetContext } from '../contexts/AssetContext';
import { useNavigate } from 'react-router-dom';
import SyncButton from './SyncButton';

const Dashboard = () => {
  const { state } = useAssetContext();
  const [filteredAssets, setFilteredAssets] = useState([]);
  const [filter, setFilter] = useState('all');
  const navigate = useNavigate();

  // Filter assets based on selected exchange
  useEffect(() => {
    if (filter === 'all') {
      setFilteredAssets(state.assets);
    } else {
      // For hyperliquid, we need to match any hyperliquid-* source
      if (filter === 'hyperliquid') {
        setFilteredAssets(state.assets.filter(asset => asset.source.startsWith('hyperliquid')));
      } else {
        setFilteredAssets(state.assets.filter(asset => asset.source === filter));
      }
    }
  }, [state.assets, filter]);

  // Group assets by source for the summary view
  const groupedAssets = filteredAssets.reduce((groups, asset) => {
    // Extract the base source (e.g., 'hyperliquid-spot' becomes 'hyperliquid')
    const baseSource = asset.source.split('-')[0];
    if (!groups[baseSource]) {
      groups[baseSource] = {
        name: baseSource.charAt(0).toUpperCase() + baseSource.slice(1), // Capitalize first letter
        assets: [],
        totalValue: 0
      };
    }
    groups[baseSource].assets.push(asset);
    groups[baseSource].totalValue += (asset.value || 0);
    return groups;
  }, {});

  const handleSourceClick = (source) => {
    if (source === 'hyperliquid') {
      // For Hyperliquid, navigate to the hyperliquid detail view
      navigate('/detail/hyperliquid');
    } else {
      // For other sources, navigate to the detail view with the source parameter
      navigate(`/detail/${source}`);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-800">Portfolio Dashboard</h2>
        <SyncButton />
      </div>
      
      {state.lastUpdated && (
        <div className="text-sm text-gray-500">
          Last updated: {new Date(state.lastUpdated).toLocaleString()}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-700">Total Assets</h3>
          <p className="text-2xl font-bold text-gray-900">
            ${state.assets.reduce((sum, asset) => sum + (asset.value || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-700">Sources</h3>
          <p className="text-2xl font-bold text-gray-900">
            {Object.keys(groupedAssets).length}
          </p>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-700">Assets</h3>
          <p className="text-2xl font-bold text-gray-900">{state.assets.length}</p>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-700">Status</h3>
          <p className={`text-2xl font-bold ${state.loading ? 'text-yellow-500' : 'text-green-500'}`}>
            {state.loading ? 'Updating...' : 'Up to date'}
          </p>
        </div>
      </div>

      <div className="bg-white p-4 rounded-lg shadow">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-700">Assets by Source</h3>
          <div className="flex space-x-2">
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Sources</option>
              <option value="binance">Binance</option>
              <option value="okx">OKX</option>
              <option value="hyperliquid">Hyperliquid</option>
              <option value="aster">Aster</option>
              <option value="defi">DeFi</option>
              <option value="polymarket">Polymarket</option>
            </select>
          </div>
        </div>

        {/* Grouped View - Summary of assets by source */}
        <div className="space-y-4">
          {Object.entries(groupedAssets).map(([source, group]) => (
            <div 
              key={source} 
              className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => handleSourceClick(source)}
            >
              <div className="flex justify-between items-center">
                <div>
                  <h4 className="text-lg font-medium text-gray-900 capitalize">{group.name}</h4>
                  <p className="text-gray-600">{group.assets.length} assets</p>
                </div>
                <div className="text-right">
                  <p className="text-xl font-bold text-gray-900">
                    ${group.totalValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                  <p className="text-sm text-gray-500">Total Value</p>
                </div>
                <div className="ml-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
              
              {/* Show top assets for quick overview */}
              <div className="mt-3 flex flex-wrap gap-2">
                {group.assets.slice(0, 5).map((asset, idx) => (
                  <span key={idx} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    {asset.symbol}: ${asset.value?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                ))}
                {group.assets.length > 5 && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    +{group.assets.length - 5} more
                  </span>
                )}
              </div>
            </div>
          ))}

          {Object.keys(groupedAssets).length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No assets found. Please configure your exchanges and sync your data.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;